'use client';

import { swissCantonLabel } from '@/lib/countries';
import { useEffect, useRef, useState, useTransition } from 'react';
import { useRouter, useSearchParams } from 'next/navigation';
import { Clock, Loader2, Search, Sparkles, X } from 'lucide-react';
import { toast } from 'sonner';
import { useQueryClient } from '@tanstack/react-query';
import { useSession } from '@/hooks/use-session';
import { tierForSession } from '@/lib/search-gating';
import { useAiSearch, AiSearchRequestError } from '@/hooks/use-ai-search';
import { aiSearchKey } from '@/lib/ai-search';
import { getAiSearchProgress, resetAiSearchProgress, setAiSearchProgress } from '@/lib/ai-search-progress';
import { CREDIT_BALANCE_QUERY_KEY, type CreditBalance } from '@/hooks/use-credit-balance';
import { CreditsUsedPopup, type CreditsUsedSnapshot } from '@/new_components/shared/CreditsUsedPopup';
import { InsufficientCreditsDialog } from '@/new_components/shared/InsufficientCreditsDialog';
import { FilterLockBadge } from '@/new_components/searchresults/FilterLockBadge';
import { LocaleLink } from '@/components/locale-link';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { useTranslation } from 'react-i18next';
import { trackHomepageSearch } from '@/lib/analytics';
import { cn } from '@/lib/utils';
import { generateCompanySlug } from '@/lib/company-utils';
import { useDebounce } from '@/hooks/use-debounce';
import { useLocale, useLocalePath } from '@/hooks/use-locale';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { ResultRowContent, type SuggestRow } from './LandingSearchResultRow';

const AI_MODE_KEY = 'ai-search-mode';
const AI_QUERY_KEY = 'ai-search-query';

function publishAiMode(enabled: boolean, query: string) {
  try {
    sessionStorage.setItem(AI_MODE_KEY, String(enabled));
    if (enabled) sessionStorage.setItem(AI_QUERY_KEY, query);
    else sessionStorage.removeItem(AI_QUERY_KEY);
  } catch { /* unavailable storage */ }
  window.dispatchEvent(new CustomEvent('firmendata:ai-search-mode', { detail: { enabled, query: enabled ? query : '' } }));
}

export default function LandingSearch({
  compact = false,
  largeOnMobile = false,
  trailingButton,
  className,
}: {
  compact?: boolean;
  largeOnMobile?: boolean;
  /** Optional button rendered inside the right edge of the compact pill — used
   *  on the mobile /search header to host the filter toggle. */
  trailingButton?: React.ReactNode;
  /** Extra classes merged onto the root wrapper — used to relax the default
   *  `max-w-xl` cap when the search is hosted in a wider slot (e.g. the
   *  expanded /search header). */
  className?: string;
} = {}) {
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState('');
  const [aiMode, setAiMode] = useState(false);
  const [aiExample, setAiExample] = useState(0);
  const [aiError, setAiError] = useState<'noFilters' | 'rejected' | 'unavailable' | 'rateLimited' | null>(null);
  const [lockOpen, setLockOpen] = useState(false);
  const [creditsOpen, setCreditsOpen] = useState(false);
  const [creditSnapshot, setCreditSnapshot] = useState<CreditsUsedSnapshot | null>(null);
  const [isNavigationPending, startTransition] = useTransition();
  const aiSearch = useAiSearch();
  const queryClient = useQueryClient();
  const { data: session } = useSession();
  const [searchResults, setSearchResults] = useState<SuggestRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDropdown, setShowDropdown] = useState(false);
  const [lastSearches, setLastSearches] = useState<string[]>([]);
  const [showLastSearches, setShowLastSearches] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  // True only while activeIndex was set via ArrowUp/ArrowDown. Enter must
  // honor a highlighted row ONLY for keyboard navigation: a dropdown that
  // opens under the mouse cursor fires onMouseEnter immediately, and Enter
  // would then silently re-run the hovered "last search" instead of the
  // (possibly just-cleared) input text.
  const keyboardNavRef = useRef(false);
  const router = useRouter();
  const { locale, internalPathname } = useLocale();
  const onSearchPage = internalPathname === '/search';
  const isPro = tierForSession(session) === 'pro';
  const inputRef = useRef<HTMLInputElement>(null);
  const aiModeRef = useRef(false);
  const aiPhase = aiSearch.isPending ? 'interpreting' : isNavigationPending ? 'results' : null;
  const aiBusy = aiPhase !== null;
  const sawNavigationPendingRef = useRef(false);
  useEffect(() => {
    if (isNavigationPending) {
      sawNavigationPendingRef.current = true;
    } else if (sawNavigationPendingRef.current) {
      sawNavigationPendingRef.current = false;
      if (getAiSearchProgress().phase === 'loading') resetAiSearchProgress();
    }
  }, [isNavigationPending]);
  useEffect(() => {
    if (!onSearchPage) return;
    const timer = window.setTimeout(() => {
      try {
        if (sessionStorage.getItem(AI_MODE_KEY) === 'true') {
          aiModeRef.current = true;
          setAiMode(true);
          setQuery(sessionStorage.getItem(AI_QUERY_KEY) ?? '');
          suppressDropdownRef.current = true;
          setShowDropdown(false);
          setShowLastSearches(false);
        }
      } catch { /* unavailable storage */ }
    }, 0);
    return () => window.clearTimeout(timer);
  }, [onSearchPage]);
  useEffect(() => {
    const onMode = (event: Event) => {
      if (!onSearchPage) return;
      const detail = (event as CustomEvent<{ enabled: boolean; query: string }>).detail;
      aiModeRef.current = detail.enabled;
      setAiMode(detail.enabled);
      setQuery(detail.query);
      setAiError(null);
      suppressDropdownRef.current = true;
      setShowDropdown(false);
      setShowLastSearches(false);
    };
    const onQuery = (event: Event) => {
      if (onSearchPage) setQuery((event as CustomEvent<string>).detail);
    };
    window.addEventListener('firmendata:ai-search-mode', onMode);
    window.addEventListener('firmendata:ai-search-query', onQuery);
    return () => {
      window.removeEventListener('firmendata:ai-search-mode', onMode);
      window.removeEventListener('firmendata:ai-search-query', onQuery);
    };
  }, [onSearchPage]);
  useEffect(() => {
    if (!aiMode) return;
    const timer = setInterval(() => setAiExample((value) => (value + 1) % 3), 4000);
    return () => clearInterval(timer);
  }, [aiMode]);
  useEffect(() => {
    const edit = (event: Event) => {
      if (!onSearchPage || !inputRef.current?.getClientRects().length) return;
      const editedQuery = (event as CustomEvent<string>).detail;
      publishAiMode(true, editedQuery);
      requestAnimationFrame(() => {
        const input = inputRef.current;
        input?.focus();
        input?.setSelectionRange(editedQuery.length, editedQuery.length);
        if (largeOnMobile) window.scrollTo({ top: 0, behavior: 'smooth' });
      });
    };
    window.addEventListener('firmendata:edit-ai-search', edit);
    return () => window.removeEventListener('firmendata:edit-ai-search', edit);
  }, [onSearchPage, largeOnMobile]);
  const localePath = useLocalePath();
  const searchContainerRef = useRef<HTMLDivElement>(null);
  const debouncedQuery = useDebounce(query, 200);
  // Set when a search is submitted (Enter/click) so a still-pending debounced
  // fetch can't pop the autocomplete dropdown back open after we've navigated.
  // Cleared the moment the user re-engages the input (types or focuses).
  const suppressDropdownRef = useRef(false);

  // On /search the bar must mirror the URL's keyword. Its state alone can't:
  // the header persists across the soft navigation from the homepage search,
  // so it arrives at /search?q=… still holding '', and a saved search or
  // back/forward can change `q` without a keystroke here. useSearchParams
  // tracks all of those (including the page's own shallow pushState); on any
  // other page the bar is a fresh search box and user text is left alone.
  const searchParams = useSearchParams();
  const urlQ =
    internalPathname === '/search' ? (searchParams.get('q') ?? '') : null;
  useEffect(() => {
    if (urlQ === null || aiModeRef.current) return;
    setQuery(urlQ);
    // Programmatic change — the debounced fetch must not pop the dropdown.
    suppressDropdownRef.current = true;
    setShowDropdown(false);
    setShowLastSearches(false);
  }, [urlQ]);

  const fetchLastSearches = async () => {
    try {
      const res = await fetch('/api/search/last-searches');
      if (res.ok) {
        const data = await res.json();
        const keywords = data.keywords ?? [];
        setLastSearches(keywords);
        return keywords;
      }
    } catch {
      // silently ignore
    }
    return [];
  };

  const saveLastSearch = async (keyword: string) => {
    try {
      await fetch('/api/search/last-searches', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword }),
      });
    } catch {
      // silently ignore
    }
  };

  const discardLastSearch = async (keyword: string) => {
    setLastSearches((prev) => prev.filter((k) => k !== keyword));
    try {
      await fetch('/api/search/last-searches', {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ keyword }),
      });
      const refreshed = await fetchLastSearches();
      if (refreshed.length === 0) setShowLastSearches(false);
    } catch {
      // silently ignore
    }
  };

  useEffect(() => {
    const performSearch = async () => {
      // A search was just submitted — don't reopen the dropdown for the query
      // that's already been searched. Cleared when the user re-engages.
      if (suppressDropdownRef.current || aiMode) return;
      if (debouncedQuery.trim().length < 1) {
        setSearchResults([]);
        setShowDropdown(false);
        return;
      }

      setIsLoading(true);
      setShowDropdown(true);
      try {
        // The fast prefix autocomplete (biggest revenue first, from one
        // letter) — not the full search, which this dropdown used to run on
        // every keystroke from three letters.
        const params = new URLSearchParams({ q: debouncedQuery.trim(), limit: '8' });
        const res = await fetch(`/api/companies/suggest?${params}`);
        const json = res.ok ? ((await res.json()) as { results?: SuggestRow[] }) : {};
        setSearchResults(json.results ?? []);
      } catch {
        // silently handle search errors
      } finally {
        setIsLoading(false);
      }
    };

    performSearch();
  }, [debouncedQuery, aiMode]);

  // Reset active index when results or dropdown visibility change
  useEffect(() => {
    setActiveIndex(-1);
    keyboardNavRef.current = false;
  }, [searchResults, showDropdown, showLastSearches]);

  // Scroll active item into view
  useEffect(() => {
    if (activeIndex >= 0 && searchContainerRef.current) {
      const activeEl = searchContainerRef.current.querySelector(
        '[data-active="true"]',
      );
      if (activeEl) activeEl.scrollIntoView({ block: 'nearest' });
    }
  }, [activeIndex]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(event.target as Node)
      ) {
        setShowDropdown(false);
        setShowLastSearches(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  /**
   * Empty-bar submit: search without a keyword. On /search itself the header
   * input *is* the page's keyword field (the sidebar's own is hidden there),
   * so clearing it means "drop the keyword", not "drop my whole search" —
   * the other applied filters are kept and only `q` (plus the now-meaningless
   * relevance sort and the page offset) leaves the URL. Everywhere else it
   * simply opens the default search.
   */
  const goToKeywordlessSearch = () => {
    const searchPath = localePath('/search');
    if (internalPathname !== '/search') {
      router.push(searchPath);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.delete('q');
    params.delete('offset');
    const sort = params.get('sort');
    if (sort && sort.startsWith('relevance')) params.delete('sort');
    const qs = params.toString();
    if (qs) {
      router.push(`${searchPath}?${qs}`);
    } else {
      // The bare default search needs a full page load: soft-navigating there
      // keeps the previous results on screen, because the server deliberately
      // skips the default search (searchSkipped) and the client's one-shot
      // initial fetch has already run. Same reason the nav "Search" link uses
      // `hardNavigate` (see locale-link.tsx).
      window.location.assign(searchPath);
    }
  };

  const handleSearch = (e?: React.FormEvent) => {
    e?.preventDefault();
    if (onSearchPage && aiMode) {
      const trimmed = query.trim();
      if (!trimmed || aiBusy) return;
      setAiError(null);
      suppressDropdownRef.current = true;
      setShowDropdown(false);
      setShowLastSearches(false);
      const startedAt = Date.now();
      setAiSearchProgress({ phase: 'interpreting', query: trimmed, startedAt });
      const modelLocale = i18n.language === 'de' || i18n.language === 'ch' ? 'de' : 'en';
      aiSearch.mutate({ query: trimmed, locale: modelLocale, uiLocale: locale }, {
        onSuccess: (result) => {
          if (result.charged > 0) setCreditSnapshot({ charged: result.charged, balance: null });
          void queryClient.invalidateQueries({ queryKey: CREDIT_BALANCE_QUERY_KEY }).then(() => {
            const balance = queryClient.getQueryData<CreditBalance>(CREDIT_BALANCE_QUERY_KEY)?.balance;
            if (balance !== undefined) setCreditSnapshot((current) => current && current.charged === result.charged
              ? { ...current, balance } : current);
          }).catch(() => {});
          if (result.filtersApplied === 0) {
            resetAiSearchProgress('error');
            setAiError(result.notes.some((note) => note.kind === 'backend' && note.code === 'rejected')
              ? 'rejected' : 'noFilters');
            return;
          }
          const resultParams = new URL(result.url, window.location.origin).searchParams;
          const targetKey = aiSearchKey(resultParams);
          try { sessionStorage.setItem(`ai-search:${targetKey}`, JSON.stringify({
            url: result.url, query: trimmed, notes: result.notes, unmapped: result.unmapped,
            id: result.id, charged: result.charged,
          })); } catch { /* unavailable storage */ }
          if (targetKey === aiSearchKey(new URLSearchParams(window.location.search))) {
            resetAiSearchProgress();
            window.dispatchEvent(new Event('firmendata:ai-search-banner'));
            return;
          }
          setAiSearchProgress({ phase: 'loading', query: trimmed, startedAt, targetKey });
          startTransition(() => router.push(result.url));
        },
        onError: (error) => {
          resetAiSearchProgress('error');
          if (error instanceof AiSearchRequestError && error.status === 402) {
            if (error.code === 'auth_required') setLockOpen(true);
            else setCreditsOpen(true);
          }
          else {
            toast.error(error instanceof AiSearchRequestError && error.status === 429
              ? t('search.ai.rateLimited') : t('search.ai.unavailable'));
            setAiError(error instanceof AiSearchRequestError && error.status === 429
              ? 'rateLimited' : 'unavailable');
          }
        },
      });
      return;
    }
    if (query.trim()) {
      trackHomepageSearch(query.trim());
      saveLastSearch(query.trim());
      const qs = new URLSearchParams({ q: query.trim(), sort: 'relevance-desc' });
      router.push(`${localePath('/search')}?${qs.toString()}`);
    } else {
      goToKeywordlessSearch();
    }
    suppressDropdownRef.current = true;
    setShowDropdown(false);
    setShowLastSearches(false);
  };

  const handleResultClick = (item: SuggestRow) => {
    trackHomepageSearch(item.name?.trim() || '');
    if (item.name?.trim()) {
      saveLastSearch(item.name.trim());
    }
    const slug = generateCompanySlug(
      {
        name: item.name,
        registerCourt: item.registerCourt,
        registerType: item.registerType,
        registerId: item.registerId,
      },
      locale,
    );
    router.push(slug);
    setShowDropdown(false);
  };

  const handleQueryChange = (value: string) => {
    setAiError(null);
    // User is editing again — allow the dropdown to reopen.
    suppressDropdownRef.current = false;
    setQuery(value);
    if (aiMode && onSearchPage) {
      try { sessionStorage.setItem(AI_QUERY_KEY, value); } catch { /* unavailable storage */ }
      window.dispatchEvent(new CustomEvent('firmendata:ai-search-query', { detail: value }));
      setShowDropdown(false);
      setShowLastSearches(false);
      return;
    }
    if (value.trim().length >= 1) {
      setShowDropdown(true);
      setShowLastSearches(false);
    } else if (value.length === 0) {
      setShowDropdown(false);
      setShowLastSearches(lastSearches.length > 0);
    } else {
      setShowDropdown(false);
      setShowLastSearches(false);
    }
  };
  const aiToggleLabel = t(isPro ? 'search.ai.mode' : session ? 'search.ai.proHint' : 'search.ai.guestHint');
  const compactControlClass = 'inline-flex size-10 lg:size-8 shrink-0 items-center justify-center rounded-full hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary transition-colors';

  return (
    <div
      className={cn(
        compact ? 'relative w-full max-w-xl' : 'relative w-full max-w-3xl',
        className,
      )}
      ref={searchContainerRef}
    >
      <form
        onSubmit={handleSearch}
        className={
          compact
            ? 'relative flex w-full items-center'
            : 'relative flex flex-col sm:flex-row w-full items-center gap-2 sm:gap-3'
        }
      >
        <div className={cn(
          'relative flex-1 w-full group',
          compact && 'flex min-w-0 items-center rounded-full border px-1 shadow-sm focus-within:border-primary focus-within:ring-2 focus-within:ring-primary/20 transition-colors',
          compact && (largeOnMobile ? 'h-12 lg:h-10' : 'h-10'),
          compact && (aiMode && onSearchPage ? 'border-primary/60 bg-primary/5' : 'bg-white dark:bg-zinc-900'),
        )}>
          {compact ? (
            <button
              type="submit"
              aria-label={aiMode && onSearchPage ? t('search.ai.submit') : t('common.buttons.search')}
              title={aiMode && onSearchPage ? t('search.ai.price') : undefined}
              disabled={aiBusy}
              onClick={(e) => {
                if (aiMode && onSearchPage) return;
                if (!query.trim()) {
                  e.preventDefault();
                  goToKeywordlessSearch();
                  setShowDropdown(false);
                  setShowLastSearches(false);
                }
              }}
              className={cn(compactControlClass, 'text-muted-foreground hover:text-foreground')}
            >
              <Search className="h-4 w-4" />
            </button>
          ) : (
            <Search className="absolute left-3 sm:left-4 top-1/2 h-3.5 w-3.5 sm:h-4 sm:w-4 -translate-y-1/2 text-muted-foreground group-focus-within:text-primary transition-colors" />
          )}
          <Input
            ref={inputRef}
            value={query}
            disabled={aiBusy}
            aria-busy={aiBusy}
            maxLength={aiMode && onSearchPage ? 300 : undefined}
            onChange={(e) => handleQueryChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.keyCode === 13) {
                if (aiMode && onSearchPage) { e.preventDefault(); handleSearch(); return; }
                if (showDropdown && searchResults.length > 0 && keyboardNavRef.current && activeIndex >= 0 && activeIndex < searchResults.length) {
                  e.preventDefault();
                  handleResultClick(searchResults[activeIndex]);
                } else if (showLastSearches && lastSearches.length > 0 && keyboardNavRef.current && activeIndex >= 0) {
                  e.preventDefault();
                  const keyword = lastSearches[activeIndex];
                  setQuery(keyword);
                  // setQuery retriggers the debounced fetch; suppress it so the
                  // dropdown doesn't reopen after navigating.
                  suppressDropdownRef.current = true;
                  setShowLastSearches(false);
                  router.push(
                    `${localePath('/search')}?q=${encodeURIComponent(keyword)}&sort=relevance-desc`,
                  );
                } else {
                  // No item highlighted or no dropdown — go to search page
                  e.preventDefault();
                  handleSearch();
                }
              } else if (showDropdown && searchResults.length > 0) {
                const itemCount = searchResults.length + 1;
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  keyboardNavRef.current = true;
                  setActiveIndex((prev) => (prev + 1) % itemCount);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  keyboardNavRef.current = true;
                  setActiveIndex((prev) =>
                    prev <= 0 ? itemCount - 1 : prev - 1,
                  );
                } else if (e.key === 'Escape') {
                  setShowDropdown(false);
                  setActiveIndex(-1);
                }
              } else if (showLastSearches && lastSearches.length > 0) {
                if (e.key === 'ArrowDown') {
                  e.preventDefault();
                  keyboardNavRef.current = true;
                  setActiveIndex((prev) => (prev + 1) % lastSearches.length);
                } else if (e.key === 'ArrowUp') {
                  e.preventDefault();
                  keyboardNavRef.current = true;
                  setActiveIndex((prev) =>
                    prev <= 0 ? lastSearches.length - 1 : prev - 1,
                  );
                } else if (e.key === 'Escape') {
                  setShowLastSearches(false);
                  setActiveIndex(-1);
                }
              }
            }}
            onFocus={() => {
              if (aiMode && onSearchPage) return;
              // Re-focusing the input counts as re-engaging — allow reopening.
              suppressDropdownRef.current = false;
              if (query.trim().length >= 1) {
                setShowDropdown(true);
              } else if (query.length === 0) {
                fetchLastSearches().then((keywords) => {
                  if (keywords.length > 0) setShowLastSearches(true);
                });
              }
            }}
            placeholder={aiMode && onSearchPage ? t(`search.ai.examples.${aiExample}`) : t('search.placeholder')}
            className={
              compact
                ? 'h-full flex-1 min-w-0 border-0 bg-transparent px-0 text-ellipsis !text-base shadow-none focus-visible:ring-0 font-medium'
                : cn('!h-9 sm:!h-12 w-full rounded-none border bg-white dark:bg-zinc-900 pl-9 sm:pl-12 !text-base shadow-sm focus-visible:ring-primary/20 focus-visible:border-primary transition-all font-medium', onSearchPage ? 'pr-28' : 'pr-4')
            }
          />
          {compact && query !== '' && (
            <button
              type="button"
              aria-label={t('search.clearQuery')}
              disabled={aiBusy}
              onMouseDown={(event) => event.preventDefault()}
              onClick={() => {
                handleQueryChange('');
                inputRef.current?.focus();
              }}
              className={cn(compactControlClass, 'text-muted-foreground')}
            >
              <X className="size-4" aria-hidden />
            </button>
          )}
          <div className={compact ? 'flex shrink-0 items-center' : 'contents'}>
            {compact && (onSearchPage || trailingButton) && <span aria-hidden className="mx-1 h-5 w-px shrink-0 bg-border" />}
            {onSearchPage && (
              <button
                type="button"
                aria-pressed={aiMode}
                aria-label={compact ? aiToggleLabel : t('search.ai.mode')}
                title={compact ? aiToggleLabel : t('search.ai.mode')}
                onMouseDown={(event) => event.preventDefault()}
                onClick={() => {
                  if (!isPro) { setLockOpen(true); return; }
                  publishAiMode(!aiMode, query);
                }}
                disabled={aiBusy}
                className={compact
                  ? cn(compactControlClass, 'relative gap-1 lg:w-auto lg:px-2 cursor-pointer text-xs disabled:cursor-wait', aiMode ? 'bg-primary text-primary-foreground hover:bg-primary/90' : 'text-muted-foreground')
                  : `absolute right-2 lg:right-4 top-1/2 -translate-y-1/2 flex min-h-9 cursor-pointer items-center gap-1 rounded-full px-1.5 text-xs disabled:cursor-wait ${aiMode ? 'bg-primary text-primary-foreground hover:opacity-90' : 'text-muted-foreground hover:bg-accent'}`}
              >
                {aiBusy ? <Loader2 className="size-4 motion-safe:animate-spin" aria-hidden /> : <Sparkles className="size-4" aria-hidden />}
                <span className={compact ? 'hidden lg:inline' : undefined}>{t('search.ai.shortLabel')}</span>
                {!isPro && <FilterLockBadge lock={session ? 'pro' : 'user'} iconOnly={compact} className={compact ? 'absolute top-1 right-1 lg:static lg:ml-0.5' : undefined} />}
              </button>
            )}
            {compact && trailingButton}
          </div>
        </div>
        <input type="submit" className="sr-only" tabIndex={-1} aria-hidden />
        {!compact && (
          <Button
            type="submit"
            size="lg"
            className="hidden sm:inline-flex h-12 w-auto rounded-none px-10 text-base font-semibold transition-all"
          >
            {t('common.buttons.search')}
          </Button>
        )}
      </form>

      {onSearchPage && <span className="sr-only" role="status" aria-live="polite">{aiPhase ? t(aiPhase === 'interpreting' ? 'search.ai.interpreting' : 'search.ai.loadingResults') : ''}</span>}
      {onSearchPage && aiError && !aiBusy && (
        <div role="alert" className="absolute top-full z-50 mt-1 rounded border bg-popover p-2 text-sm shadow">
          {t(aiError === 'rejected' ? 'search.ai.notes.rejected' : `search.ai.${aiError}`)}
          <button type="button" className="ml-2 underline" onClick={() => { setAiMode(false); setAiError(null); const qs = new URLSearchParams({ q: query.trim(), sort: 'relevance-desc' }); router.push(`${localePath('/search')}?${qs}`); }}>{t('search.ai.searchKeyword')}</button>
        </div>
      )}

      {showDropdown && !suppressDropdownRef.current && !aiMode && query.trim().length >= 1 && (
        <div className="absolute top-full mt-2 w-full border bg-popover/80 backdrop-blur-md text-popover-foreground shadow-xl z-50 overflow-hidden animate-in fade-in-0 zoom-in-95">
          {isLoading ? (
            <div className="p-8 text-sm text-muted-foreground text-center">
              {t('search.searching')}
            </div>
          ) : searchResults.length > 0 ? (
            <div className="max-h-[400px] overflow-auto py-1">
              {searchResults.map((item, index) => (
                <div
                  key={item.euId}
                  data-active={activeIndex === index}
                  onClick={() => handleResultClick(item)}
                  onMouseEnter={() => setActiveIndex(index)}
                  className={`flex cursor-pointer items-center gap-2 px-3 py-1.5 transition-colors ${activeIndex === index ? 'bg-accent/50 text-accent-foreground' : 'hover:bg-accent/50 hover:text-accent-foreground'}`}
                >
                  {item.faviconUrl && (
                    <span className="flex h-5 w-5 items-center justify-center shrink-0">
                      <img
                        src={item.faviconUrl}
                        alt=""
                        className="h-5 w-5 object-contain"
                        onError={(e) => {
                          const el = (e.target as HTMLImageElement)
                            .parentElement;
                          if (el) el.style.display = 'none';
                        }}
                      />
                    </span>
                  )}
                  <ResultRowContent
                    name={item.name?.trim() ?? ''}
                    suffix={
                      [
                        item.registerType === 'CH-HR'
                          ? swissCantonLabel(item.registerCourt, i18n.language)
                          : item.registerCourt,
                        item.registerId,
                      ]
                        .filter(Boolean)
                        .join(' · ') || null
                    }
                  />
                </div>
              ))}
              <div
                data-active={activeIndex === searchResults.length}
                className={`p-3 text-center text-xs font-medium text-primary cursor-pointer transition-colors border-t ${activeIndex === searchResults.length ? 'bg-muted/50' : 'bg-muted/30 hover:bg-muted/50'}`}
                onClick={handleSearch}
                onMouseEnter={() => setActiveIndex(searchResults.length)}
              >
                {t('search.showAllResults')}
              </div>
            </div>
          ) : (
            <div className="p-8 text-sm text-muted-foreground text-center">
              {t('search.noResults', { query })}
            </div>
          )}
        </div>
      )}

      {showLastSearches && !suppressDropdownRef.current && !aiMode && !showDropdown && lastSearches.length > 0 && (
        <div className="absolute top-full mt-2 w-full border bg-popover/80 backdrop-blur-md text-popover-foreground shadow-xl z-50 overflow-hidden animate-in fade-in-0 zoom-in-95">
          <div className="py-2">
            <div className="px-5 py-2 text-xs font-medium text-muted-foreground">
              {t('search.recentSearches', 'Recent Searches')}
            </div>
            {lastSearches.map((keyword, idx) => (
              <div
                key={`${keyword}-${idx}`}
                data-active={activeIndex === idx}
                onClick={() => {
                  setQuery(keyword);
                  suppressDropdownRef.current = true;
                  setShowLastSearches(false);
                  router.push(
                    `${localePath('/search')}?q=${encodeURIComponent(keyword)}&sort=relevance-desc`,
                  );
                }}
                onMouseEnter={() => setActiveIndex(idx)}
                className={`flex cursor-pointer items-center gap-3 px-5 py-3 transition-colors mx-1 rounded-lg ${activeIndex === idx ? 'bg-accent text-accent-foreground' : 'hover:bg-accent hover:text-accent-foreground'}`}
              >
                <Clock className="h-4 w-4 text-muted-foreground shrink-0" />
                <span className="text-sm font-medium truncate flex-1 text-left">
                  {keyword}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    discardLastSearch(keyword);
                  }}
                  className="p-1 rounded-md hover:bg-muted-foreground/20 text-muted-foreground shrink-0"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
      <InsufficientCreditsDialog open={creditsOpen} onOpenChange={setCreditsOpen} />
      <CreditsUsedPopup snapshot={creditSnapshot} onClose={() => setCreditSnapshot(null)} />
      <Dialog open={lockOpen} onOpenChange={setLockOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t(session ? 'search.proFeature.title' : 'search.accountRequired.title')}</DialogTitle>
            <DialogDescription>{t(session ? 'search.ai.proRequired' : 'search.ai.signInRequired')}</DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setLockOpen(false)}>{t('common.buttons.close')}</Button>
            {session ? <Button asChild><LocaleLink href="/plans" onClick={() => setLockOpen(false)}>{t('search.proFeature.cta')}</LocaleLink></Button>
              : <Button onClick={() => { window.location.href = `/auth/login?screen_hint=signup&returnTo=${encodeURIComponent(window.location.pathname)}`; }}>{t('search.accountRequired.cta')}</Button>}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
